import React, { useCallback, useMemo, useState } from 'react';
import { FlatList, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, startOfDay, endOfDay, format, } from 'date-fns';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import { TextButton } from 'design-system';
import SectionHeader from 'design-system/SectionHeader';
import { options as LIST_FREQUENCY } from 'design-system/dateFilter/constants';
import createStyleSheets from 'utils/createStyleSheets';
import { formatPeriodDate } from 'utils/formatting';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import useDateFilter from 'design-system/dateFilter';
const keyExtractor = (item) => item;
const [weekly, monthly, quarterly, yearly, custom] = LIST_FREQUENCY;
const KeyboardDates = ({ activeDateFilter, onClickFilter, onRemoveFilter, contentContainerStyle }) => {
    const [show, hide] = useDateFilter();
    const [selectedPeriod, setSelectedPeriod] = useState(activeDateFilter?.title);
    const onSave = (data) => {
        if (!data.type) {
            return;
        }
        setSelectedPeriod(data.type);
        hide();
        let startDate = null;
        let endDate = null;
        let title = '';
        let badgeText = '';
        switch (data.type) {
            case weekly: // weekly
                if ('from' in data && 'to' in data) {
                    startDate = data.from;
                    endDate = data.to;
                    title = weekly;
                    badgeText = formatPeriodDate({ from: startDate, to: endDate }, undefined, undefined, undefined, true);
                }
                break;
            case monthly: // monthly
                if ('year' in data && 'month' in data) {
                    const { year, month } = data;
                    const date = new Date(year, month);
                    startDate = startOfMonth(date);
                    endDate = endOfMonth(date);
                    title = monthly;
                    badgeText = `${format(date, 'MMMM')} ${year}`;
                }
                break;
            case quarterly: // quarterly
                if ('year' in data && 'quarter' in data) {
                    const { year, quarter } = data;
                    const date = new Date(year, (quarter - 1) * 3);
                    startDate = startOfQuarter(date);
                    endDate = endOfQuarter(date);
                    title = quarterly;
                    badgeText = `Q${quarter} ${year}`;
                }
                break;
            case yearly: // yearly
                if ('year' in data) {
                    const { year } = data;
                    const date = new Date(year, 0);
                    startDate = startOfYear(date);
                    endDate = endOfYear(date);
                    title = yearly;
                    badgeText = `Year ${year}`;
                }
                break;
            case custom: // custom
                if ('from' in data && 'to' in data && data.to !== undefined) {
                    startDate = data.from;
                    endDate = data.to;
                }
                else if ('from' in data) {
                    startDate = startOfDay(data.from);
                    endDate = endOfDay(data.from);
                }
                title = custom;
                if (startDate && endDate) {
                    badgeText = formatPeriodDate({ from: startDate, to: endDate }, undefined, undefined, undefined, true);
                }
                break;
            default:
                break;
        }
        if (startDate && endDate) {
            const value = { startDate, endDate, title };
            onClickFilter('KeyboardDates', {
                ...value,
                badgeText,
                filterType: 'DATES',
            });
        }
    };
    const styles = useStyles(styleSet);
    const colors = useColors();
    const onPressPeriod = (period) => {
        show(onSave, period);
    };
    const renderItem = ({ item }) => (<TouchableBounce style={[
            styles.optionWrapper,
            {
                ...(selectedPeriod === item ? { borderColor: colors.text.brand } : { borderColor: 'transparent' }),
            },
        ]} hoverStyle={styles.hoverOnLight} onPress={() => onPressPeriod(item)}>
      <Text Text-14 {...(selectedPeriod === item ? { Primary: true } : { Secondary: true })}>
        {item}
      </Text>
    </TouchableBounce>);
    const resetDate = useCallback(() => {
        if (activeDateFilter) {
            onRemoveFilter(activeDateFilter);
            setSelectedPeriod(undefined);
        }
    }, [activeDateFilter, onRemoveFilter]);
    const Button = useMemo(() => {
        if (activeDateFilter) {
            return <TextButton text="Reset" onPress={resetDate}/>;
        }
        return null;
    }, [activeDateFilter, resetDate]);
    return (<View style={contentContainerStyle}>
      <SectionHeader horizontalMargins verticalMargins button={Button}>
        Periods
      </SectionHeader>
      <FlatList numColumns={2} data={LIST_FREQUENCY} scrollEnabled={false} renderItem={renderItem} style={styles.list} columnWrapperStyle={styles.wrapper} keyExtractor={keyExtractor}/>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    list: {
        backgroundColor: colors.cards.onDark,
        paddingHorizontal: rem(8),
        paddingTop: rem(8),
    },
    wrapper: { flex: 1 },
    optionWrapper: {
        flex: 1,
        backgroundColor: colors.background.dark,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
        height: rem(45),
        borderRadius: rem(12),
        marginVertical: rem(8),
        paddingVertical: rem(12),
        marginHorizontal: rem(8),
        paddingHorizontal: rem(16),
    },
    hoverOnLight: {
        backgroundColor: colors.control.hoverOnLight,
    },
}));
export default KeyboardDates;
